/**
 * Board — SVG hex grid rendering the active player's environment.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Adapted for multiplayer: disabled state dims the board for non-active players.
 * data-testid="cascadia-board" per E2E guide.
 */

import type {
  AxialCoord,
  Board as CascadiaBoard,
  TileDef,
  TileRotation,
  TurnPhase,
  Wildlife,
} from "@cloudnest/cascadia-engine";
import { coordKey, TILE_DEFS_BY_ID } from "@cloudnest/cascadia-engine";
import { computeViewBox, neighbours } from "../utils/svg-hex";
import { HexCell } from "./HexCell";

export interface BoardProps {
  readonly board: CascadiaBoard;
  readonly phase: TurnPhase;
  readonly draftedWildlife?: Wildlife | null;
  /** Coord of the pending PlaceTile (ghost preview before Confirm). */
  readonly pendingPlacementCoord?: AxialCoord | null;
  /**
   * Drafted tile def for the ghost preview hex. When present, the ghost
   * renders TileFill (habitats + rotation) instead of a plain outline.
   */
  readonly ghostTileDef?: TileDef;
  /** Rotation 0..5 for the ghost preview (live rotates with ↺/↻). */
  readonly ghostRotation?: TileRotation;
  readonly onTapCoord: (coord: AxialCoord) => void;
  readonly disabled?: boolean;
}

const HEX_SIZE = 36;

function buildVisibleCoords(board: CascadiaBoard): AxialCoord[] {
  const allKeys = new Set<string>();
  const result: AxialCoord[] = [];

  const placed = Object.keys(board.cells);

  if (placed.length === 0) {
    const origin: AxialCoord = { q: 0, r: 0 };
    allKeys.add(coordKey(origin));
    result.push(origin);
    for (const n of neighbours(origin)) {
      const k = coordKey(n);
      if (!allKeys.has(k)) {
        allKeys.add(k);
        result.push(n);
      }
    }
    return result;
  }

  for (const key of placed) {
    const [q, r] = key.split(",").map(Number) as [number, number];
    const coord: AxialCoord = { q, r };
    if (!allKeys.has(key)) {
      allKeys.add(key);
      result.push(coord);
    }
    for (const n of neighbours(coord)) {
      const nk = coordKey(n);
      if (!allKeys.has(nk)) {
        allKeys.add(nk);
        result.push(n);
      }
    }
  }

  return result;
}

function computeValidTileTargets(board: CascadiaBoard): Set<string> {
  const validKeys = new Set<string>();
  const placed = Object.keys(board.cells);

  if (placed.length === 0) {
    validKeys.add("0,0");
    return validKeys;
  }

  for (const key of placed) {
    const [q, r] = key.split(",").map(Number) as [number, number];
    for (const n of neighbours({ q, r })) {
      const nk = coordKey(n);
      if (!board.cells[nk]) {
        validKeys.add(nk);
      }
    }
  }
  return validKeys;
}

function computeValidWildlifeTargets(
  board: CascadiaBoard,
  draftedWildlife: Wildlife | null | undefined,
): Set<string> {
  if (!draftedWildlife) return new Set();
  const validKeys = new Set<string>();
  for (const [key, cell] of Object.entries(board.cells)) {
    if (cell.wildlife === null && cell.landmark === null) {
      validKeys.add(key);
    }
  }
  return validKeys;
}

export function Board({
  board,
  phase,
  draftedWildlife,
  pendingPlacementCoord,
  ghostTileDef,
  ghostRotation = 0,
  onTapCoord,
  disabled = false,
}: BoardProps) {
  const visibleCoords = buildVisibleCoords(board);
  const { viewBox } = computeViewBox(visibleCoords, HEX_SIZE);
  const pendingKey = pendingPlacementCoord
    ? coordKey(pendingPlacementCoord)
    : null;

  const validTargetKeys =
    !disabled && phase === "placeTile"
      ? computeValidTileTargets(board)
      : !disabled && phase === "placeWildlife"
        ? computeValidWildlifeTargets(board, draftedWildlife)
        : new Set<string>();

  return (
    <svg
      data-testid="cascadia-board"
      data-disabled={disabled ? "true" : "false"}
      data-phase={phase}
      viewBox={viewBox}
      style={{
        width: "100%",
        maxWidth: 540,
        height: "auto",
        background: "var(--cn-cascadia-bg)",
        borderRadius: 8,
        display: "block",
        margin: "0 auto",
        opacity: disabled ? 0.7 : 1,
        transition: "opacity 0.2s",
      }}
      aria-label="Cascadia game board"
    >
      {/* SVG pattern defs for dual-encoding */}
      <defs>
        <pattern
          id="cn-cascadia-pattern-forest"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
        >
          <line
            x1="0"
            y1="0"
            x2="8"
            y2="8"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1.5"
          />
        </pattern>
        <pattern
          id="cn-cascadia-pattern-mountain"
          patternUnits="userSpaceOnUse"
          width="10"
          height="10"
        >
          <polygon
            points="5,1 9,9 1,9"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1"
          />
        </pattern>
        <pattern
          id="cn-cascadia-pattern-prairie"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
        >
          <circle cx="4" cy="4" r="1.5" fill="rgba(255,255,255,0.3)" />
        </pattern>
        <pattern
          id="cn-cascadia-pattern-river"
          patternUnits="userSpaceOnUse"
          width="12"
          height="6"
        >
          <path
            d="M0,3 Q3,0 6,3 Q9,6 12,3"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1"
          />
        </pattern>
        <pattern
          id="cn-cascadia-pattern-wetland"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
        >
          <line
            x1="0"
            y1="4"
            x2="8"
            y2="4"
            stroke="rgba(255,255,255,0.25)"
            strokeWidth="1"
          />
          <line
            x1="4"
            y1="0"
            x2="4"
            y2="8"
            stroke="rgba(255,255,255,0.25)"
            strokeWidth="1"
          />
        </pattern>
      </defs>

      {visibleCoords.map((coord) => {
        const key = coordKey(coord);
        const cell = board.cells[key] ?? null;
        const isValidTarget = validTargetKeys.has(key);
        const tileDef =
          cell !== null ? TILE_DEFS_BY_ID[cell.tileDefId] : undefined;
        return (
          <HexCell
            key={key}
            coord={coord}
            cell={cell}
            tileDef={tileDef}
            size={HEX_SIZE}
            isValidTarget={isValidTarget}
            isGhost={cell === null && key === pendingKey}
            ghostTileDef={
              cell === null && key === pendingKey ? ghostTileDef : undefined
            }
            ghostRotation={ghostRotation}
            draftedWildlife={phase === "placeWildlife" ? draftedWildlife : null}
            onTap={disabled ? () => undefined : onTapCoord}
          />
        );
      })}
    </svg>
  );
}
